/**
 * DOM attributes that wire the multi-state preview together. Owned here so
 * both the runtime (`<code-island>`) and the Designer (selection plumbing,
 * AI Assistant prompts, peer-frame layout) agree on the wire format
 * without a circular import.
 *
 * The names are intentionally namespaced under `data-wf-` so they cannot
 * collide with author-written markup, and they live on **platform-built**
 * elements — there is no public API for the author to set them.
 */

/**
 * Set on the `<code-island>` host by the Designer when a frame should
 * render a **single specific scenario**. Value is the stored scenario id.
 * When this attribute is present, the runtime mounts the user component
 * once and merges the scenario's `props` on top of `data-props`.
 *
 * The Designer-side frame sync and runtime consumption land in later slices
 * of the multi-state visual editing work (#111198); this constant is the
 * shared name they build on.
 */
export const STATE_SCENARIO_HOST_ATTR = 'data-wf-scenario-id' as const;

/**
 * Set on the `<code-island>` host alongside `STATE_SCENARIO_HOST_ATTR`
 * so click-event interception can tag selections with their scenario
 * label without round-tripping through the loader.
 */
export const STATE_SCENARIO_LABEL_HOST_ATTR = 'data-wf-scenario-label' as const;

/**
 * Set on the `<code-island>` host alongside `STATE_SCENARIO_HOST_ATTR`.
 * Holds the JSON-serialized scenario `props` so the runtime can boot the
 * user component in the scenario's configured state **without** reading the
 * scenarios from the published bundle.
 *
 * Scenarios are stored Designer-side (on the component model) rather than in
 * the author's source, so their props are delivered to the canvas-iframe
 * runtime through this host attribute instead of riding in the bundle.
 * Omitted on hosts that aren't part of a scenario frame, keeping publish/SSR
 * payloads byte-identical to non-preview renders.
 */
export const STATE_SCENARIO_PROPS_HOST_ATTR = 'data-wf-scenario-props' as const;
